import { z } from 'zod';
import { emailSchema, phoneSchema } from './auth.js';
import { brazilianStateSchema } from './tenancy.js';

export const PERSON_STATUS = ['ACTIVE', 'AWAY', 'DISMISSED'] as const;
export const PERSON_BOND = ['VOLUNTEER', 'EMPLOYEE', 'CONTRACTOR', 'GUEST'] as const;
export const SEX = ['FEMALE', 'MALE', 'UNDISCLOSED'] as const;

export const personStatusSchema = z.enum(PERSON_STATUS);
export const personBondSchema = z.enum(PERSON_BOND);
export const sexSchema = z.enum(SEX);

export type PersonStatus = z.infer<typeof personStatusSchema>;
export type PersonBond = z.infer<typeof personBondSchema>;
export type Sex = z.infer<typeof sexSchema>;

export const PERSON_STATUS_LABEL: Record<PersonStatus, string> = {
  ACTIVE: 'Ativo',
  AWAY: 'Afastado',
  DISMISSED: 'Desligado',
};

export const PERSON_BOND_LABEL: Record<PersonBond, string> = {
  VOLUNTEER: 'Voluntário',
  EMPLOYEE: 'Contratado (CLT)',
  CONTRACTOR: 'Prestador (PJ)',
  GUEST: 'Convidado',
};

export const SEX_LABEL: Record<Sex, string> = {
  FEMALE: 'Feminino',
  MALE: 'Masculino',
  UNDISCLOSED: 'Não informado',
};

/** Data no formato AAAA-MM-DD, sem fuso — data de nascimento não tem hora. */
export const isoDateSchema = z
  .string()
  .trim()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use o formato AAAA-MM-DD')
  .refine((valor) => !Number.isNaN(new Date(`${valor}T12:00:00Z`).getTime()), 'Data inválida');

/** Idade em anos completos na data de referência. */
export function idadeEm(nascimento: string, referencia: Date = new Date()): number {
  const [ano, mes, dia] = nascimento.split('-').map(Number) as [number, number, number];
  let idade = referencia.getFullYear() - ano;
  const mesAtual = referencia.getMonth() + 1;
  const diaAtual = referencia.getDate();
  if (mesAtual < mes || (mesAtual === mes && diaAtual < dia)) idade -= 1;
  return idade;
}

export function ehMenorDeIdade(nascimento: string | null | undefined, referencia?: Date): boolean {
  if (!nascimento) return false;
  return idadeEm(nascimento, referencia) < 18;
}

const basePersonSchema = z.object({
  fullName: z
    .string()
    .trim()
    .min(3, 'Informe o nome completo')
    .max(160, 'Nome longo demais')
    .refine((valor) => valor.includes(' '), 'Informe nome e sobrenome'),
  displayName: z.string().trim().max(80).nullish(),
  phone: phoneSchema.nullish(),
  email: emailSchema.nullish(),
  birthDate: isoDateSchema.nullish(),
  sex: sexSchema.default('UNDISCLOSED'),
  congregationId: z.string().uuid('Escolha uma congregação').nullish(),
  officeId: z.string().uuid().nullish(),
  bond: personBondSchema.default('VOLUNTEER'),
  status: personStatusSchema.default('ACTIVE'),
  ministrySince: isoDateSchema.nullish(),
  addressLine: z.string().trim().max(240).nullish(),
  city: z.string().trim().max(120).nullish(),
  state: brazilianStateSchema.nullish(),
  notes: z.string().trim().max(1000).nullish(),
  guardianName: z.string().trim().max(160).nullish(),
  guardianPhone: phoneSchema.nullish(),
  tagIds: z.array(z.string().uuid()).max(20).optional(),
});

/**
 * Menor de idade só entra com responsável legal (LGPD art. 14). A regra vive
 * aqui, no contrato, para valer igual no navegador e no servidor.
 */
function exigirResponsavelParaMenor(
  dados: { birthDate?: string | null; guardianName?: string | null; guardianPhone?: string | null },
  ctx: z.RefinementCtx,
): void {
  if (!ehMenorDeIdade(dados.birthDate)) return;

  if (!dados.guardianName?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['guardianName'],
      message: 'Menor de idade precisa de um responsável legal',
    });
  }
  if (!dados.guardianPhone?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['guardianPhone'],
      message: 'Informe o telefone do responsável',
    });
  }
}

export const createPersonSchema = basePersonSchema.superRefine(exigirResponsavelParaMenor);
export type CreatePersonInput = z.infer<typeof createPersonSchema>;

export const updatePersonSchema = basePersonSchema.partial().superRefine(exigirResponsavelParaMenor);
export type UpdatePersonInput = z.infer<typeof updatePersonSchema>;

export const personSchema = z.object({
  id: z.string().uuid(),
  fullName: z.string(),
  displayName: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  birthDate: z.string().nullable(),
  age: z.number().int().nullable(),
  isMinor: z.boolean(),
  sex: sexSchema,
  photoUrl: z.string().nullable(),
  addressLine: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  bond: personBondSchema,
  status: personStatusSchema,
  ministrySince: z.string().nullable(),
  notes: z.string().nullable(),
  guardianName: z.string().nullable(),
  guardianPhone: z.string().nullable(),
  congregation: z.object({ id: z.string(), name: z.string() }).nullable(),
  office: z.object({ id: z.string(), name: z.string() }).nullable(),
  tags: z.array(z.object({ id: z.string(), name: z.string(), color: z.string() })),
  ministries: z.array(
    z.object({ id: z.string(), name: z.string(), color: z.string(), role: z.string() }),
  ),
  qualifications: z.array(
    z.object({
      id: z.string(),
      functionId: z.string(),
      functionName: z.string(),
      ministryName: z.string(),
      level: z.enum(['APPRENTICE', 'QUALIFIED', 'REFERENCE']),
      validUntil: z.string().nullable(),
      expired: z.boolean(),
    }),
  ),
  createdAt: z.string().datetime(),
});
export type Person = z.infer<typeof personSchema>;

/** Resumo usado em listagens: leve o bastante para 500 linhas no celular. */
export const personSummarySchema = personSchema.pick({
  id: true,
  fullName: true,
  displayName: true,
  phone: true,
  email: true,
  status: true,
  bond: true,
  isMinor: true,
  age: true,
  photoUrl: true,
  congregation: true,
  office: true,
  tags: true,
});
export type PersonSummary = z.infer<typeof personSummarySchema>;

export const peopleQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  search: z.string().trim().max(120).optional(),
  status: personStatusSchema.optional(),
  bond: personBondSchema.optional(),
  congregationId: z.string().uuid().optional(),
  ministryId: z.string().uuid().optional(),
  tagId: z.string().uuid().optional(),
  /** Só quem está habilitado nesta função. */
  functionId: z.string().uuid().optional(),
});
export type PeopleQuery = z.infer<typeof peopleQuerySchema>;

export const ecclesiasticalOfficeSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  rank: z.number().int(),
  isSystem: z.boolean(),
  peopleCount: z.number().int().optional(),
});
export type EcclesiasticalOffice = z.infer<typeof ecclesiasticalOfficeSchema>;

export const createOfficeSchema = z.object({
  name: z.string().trim().min(2, 'Informe o nome do cargo').max(80),
  rank: z.coerce.number().int().min(1).max(999).default(100),
});

export const tagSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  color: z.string(),
  peopleCount: z.number().int().optional(),
});
export type Tag = z.infer<typeof tagSchema>;

export const createTagSchema = z.object({
  name: z.string().trim().min(2, 'Informe o nome da etiqueta').max(60),
  color: z
    .string()
    .trim()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Informe a cor em hexadecimal')
    .default('#667085'),
});

/**
 * Cargos eclesiásticos que quase toda igreja protestante brasileira usa.
 * Servem de ponto de partida; o tenant edita, renomeia e cria os seus.
 */
export const OFFICE_PRESETS: ReadonlyArray<{ name: string; rank: number }> = [
  { name: 'Pastor titular', rank: 10 },
  { name: 'Pastor auxiliar', rank: 20 },
  { name: 'Evangelista', rank: 30 },
  { name: 'Missionário', rank: 40 },
  { name: 'Presbítero', rank: 50 },
  { name: 'Diácono', rank: 60 },
  { name: 'Diaconisa', rank: 65 },
  { name: 'Obreiro', rank: 70 },
  { name: 'Cooperador', rank: 80 },
  { name: 'Líder de congregação', rank: 85 },
  { name: 'Membro', rank: 90 },
  { name: 'Congregado', rank: 95 },
  { name: 'Visitante', rank: 99 },
];
